import { useEffect, useRef, type ReactNode } from 'react';
import { useI18n } from '../i18n/i18n.js';

interface Props {
  title: string;
  children: ReactNode;
  onClose?: () => void;
  testId?: string;
  /** Media such as videos: almost the full window width instead of a narrow form. */
  wide?: boolean;
}

/** Minimal accessible modal: focus moves into the dialog, Escape closes if allowed. */
export function Dialog({ title, children, onClose, testId, wide = false }: Props) {
  const { m } = useI18n();
  const ref = useRef<HTMLDivElement>(null);
  // Focus moves in once: parents that re-render often (countdowns) must not pull the focus
  // back out of an open drop-down.
  useEffect(() => {
    ref.current?.focus();
  }, []);
  const closable = onClose !== undefined;
  const latestClose = useRef(onClose);
  useEffect(() => {
    latestClose.current = onClose;
  });
  useEffect(() => {
    if (!closable) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') latestClose.current?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [closable]);
  return (
    <div className="dialog-backdrop" role="presentation">
      <div
        ref={ref}
        className={`dialog card${wide ? ' dialog-wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        data-testid={testId}
      >
        <div className="dialog-header">
          <h2>{title}</h2>
          {onClose && (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={onClose}
              aria-label={m.common.close}
            >
              ✕
            </button>
          )}
        </div>
        {children}
      </div>
    </div>
  );
}
